import * as fuzzball from "fuzzball";
import { useMemo, useState, useCallback } from "react";

import { GAME_NAMES } from "../constants/ImmutableGameDetails";
import { CharacterEntry, FrameDataMap, GameName, MoveEntry, SearchIndex, SearchResult } from "../types";

/**
 * Alternate names accepted as a game prefix in queries.
 * e.g. "Strive Baiken fireball" resolves to GGST.
 */
const GAME_ALIASES: Record<GameName, string[]> = {
  SF6: ["6"],
  SFV: ["5", "SF5"],
  USF4: ["4", "SF4", "Ultra"],
  "3S": ["3", "SF3"],
  GGST: ["Strive", "GG"],
  "2XKO": ["twoko"],
};

/**
 * Short-form character aliases per game.
 * Exact alias hits are treated as near-perfect scores (99/100)
 * so they outrank unrelated fuzzy coincidences.
 */
const CHARACTER_ALIASES: Partial<
  Record<GameName, Record<string, string[]>>
> = {
  SF6: {
    "A.K.I.": ["aki"],
    "Chun-Li": ["chun"],
    "E.Honda": ["e"],
    Kimberly: ["kim"],
    Zangief: ["gief"],
    Dhalsim: ["sim"],
    "Dee Jay": ["deejay", "dj"],
    "M.Bison": ["m"],
  },
  SFV: {
    "Chun-Li": ["chun"],
    "E.Honda": ["e"],
    "F.A.N.G": ["fang"],
    "Zeku (Old)": ["zeku"],
    Zangief: ["gief"],
    Dhalsim: ["sim"],
  },
  GGST: {
    "I-No": ["ino"],
    "Zato-1": ["zato"],
  },
};

// Helpers

/**
 * Strips everything that isn't a lowercase letter or digit.
 * Used to compare strings without caring about punctuation,
 * spaces, or capitalisation (e.g. "236+LP" === "236lp").
 */
function normalizeSearch(input: string): string {
  return input.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Attempts to extract a game-scope prefix from the raw query string.
 * Matches both canonical keys ("SF6") and aliases ("Strive").
 * The prefix must be followed by whitespace or end-of-string.
 *
 * Returns `{ game, rest }` on a match, or `null` if no prefix is found.
 */
function extractGameScope(raw: string): { game: GameName; rest: string } | null {
  const upper = raw.trimStart();
  const upperUC = upper.toUpperCase();

  for (const key of GAME_NAMES) {
    // Try the canonical key first, then each alias.
    const candidates = [key, ...GAME_ALIASES[key]];

    for (const candidate of candidates) {
      const candidateUC = candidate.toUpperCase();

      if (upperUC.startsWith(candidateUC)) {
        const after = upper.slice(candidate.length);

        // Only accept if followed by whitespace or end-of-string,
        // so "SF6" doesn't accidentally match "SF6X" or similar.
        if (after === "" || /^\s/.test(after)) {
          return { game: key as GameName, rest: after.trimStart() };
        }
      }
    }
  }

  return null;
}

/**
 * Maps a user-typed character name through the alias table for `game`.
 * Returns the canonical character name if an exact alias match is found,
 * otherwise returns the input unchanged.
 */
function resolveCharacterAlias(game: GameName, input: string): string {
  const map = CHARACTER_ALIASES[game];
  if (!map) return input;

  const inputLC = input.toLowerCase();

  for (const canonical of Object.keys(map)) {
    for (const alias of map[canonical]) {
      if (inputLC === alias.toLowerCase()) return canonical;
    }
  }

  return input;
}

// Index builder

/**
 * Flattens `frameDataMap` into two flat arrays (characters, moves)
 * with all string variants pre-computed for fast comparisons at query time.
 * This is run once per `frameDataMap` reference change (see useMemo below).
 */
function buildIndex(frameDataMap: FrameDataMap): SearchIndex {
  const characters: CharacterEntry[] = [];
  const moves: MoveEntry[] = [];

  for (const game of GAME_NAMES) {
    const file = frameDataMap[game];
    if (!file) continue;

    for (const character of Object.keys(file)) {
      characters.push({ game, character, characterLC: character.toLowerCase() });

      const movesObj = file[character]?.moves;
      if (!movesObj) continue;

      for (const state of Object.keys(movesObj)) {
        const stateObj = movesObj[state];
        if (!stateObj) continue;

        for (const moveKey of Object.keys(stateObj)) {
          const move = stateObj[moveKey];
          const moveName = move?.moveName ?? moveKey;
          const numCmd = String(move?.numCmd ?? "");
          const plnCmd = String(move?.plnCmd ?? "");
          const cmnName = String(move?.cmnName ?? "");

          moves.push({
            game,
            character,
            state,
            moveName,
            moveNameLC: moveName.toLowerCase(),
            moveNameNorm: normalizeSearch(moveName),
            numCmd,
            plnCmd,
            cmnName,
            numCmdLC: numCmd.toLowerCase(),
            plnCmdLC: plnCmd.toLowerCase(),
            cmnNameLC: cmnName.toLowerCase(),
            numCmdNorm: normalizeSearch(numCmd),
            plnCmdNorm: normalizeSearch(plnCmd),
            cmnNameNorm: normalizeSearch(cmnName),
          });
        }
      }
    }
  }

  return { characters, moves };
}

// Hook

export interface UseFrameDataSearchOptions {
  frameDataMap: FrameDataMap;
}

/**
 * useFrameDataSearch
 *
 * Provides fuzzy full-text search across all frame data.
 *
 * Query syntax:
 *   "[game] [character] [move]"   — all parts are optional
 *
 * Examples:
 *   "Blanka"              → character results across all games
 *   "SF6"                 → all SF6 characters
 *   "SF6 Blanka"          → Blanka's character page in SF6
 *   "SF6 Blanka stand MK" → Blanka's Stand MK move in SF6
 *   "236LP"               → any move with that numeric command
 *
 * Returns:
 *   query    — current query string
 *   setQuery — update handler (stable reference)
 *   results  — up to 8 sorted SearchResult items
 *   clear    — resets query to ""
 */
export function useFrameDataSearch({ frameDataMap }: UseFrameDataSearchOptions) {
  const THRESHOLD = 80; // minimum fuzzball score (0–100) to include a result
  const MAX_RESULTS = 8;

  const [query, setQuery] = useState("");

  // Rebuild the index only when the frameDataMap reference changes.
  const index = useMemo(() => buildIndex(frameDataMap), [frameDataMap]);

  const results = useMemo<SearchResult[]>(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) return [];

    // Step 1: detect optional game-scope prefix
    // e.g. "SF6 Blanka fireball" → { game: "SF6", rest: "Blanka fireball" }
    const scoped = extractGameScope(trimmed);
    const scopedGame: GameName | null = scoped?.game ?? null;
    const rest = scoped ? scoped.rest : trimmed;

    const targetGames = new Set<GameName>(scopedGame ? [scopedGame] : GAME_NAMES);

    const filtered = {
      characters: index.characters.filter((c) => targetGames.has(c.game)),
      moves: index.moves.filter((m) => targetGames.has(m.game)),
    };

    // Step 2: game-only query → list all characters for that game
    // e.g. user typed "SF6" with nothing after it
    if (rest === "" && scopedGame) {
      const seen = new Set<string>();
      return filtered.characters
        .filter((c) => {
          const key = `${c.game}/${c.character}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((c) => ({
          kind: "character" as const,
          game: c.game,
          characterName: c.character,
          href: `/framedata/${c.game}/${encodeURIComponent(c.character)}`,
          score: 100,
        }));
    }

    const restLC = rest.toLowerCase();
    const queryNorm = normalizeSearch(rest);
    const results: SearchResult[] = [];

    // Step 3: character fuzzy search
    const charSeen = new Set<string>();

    for (const c of filtered.characters) {
      const key = `${c.game}/${c.character}`;
      if (charSeen.has(key)) continue;

      // Start with a full ratio, then blend in a discounted partial ratio
      // for substring matches on longer names.
      let score = fuzzball.ratio(restLC, c.characterLC);
      if (c.characterLC.length > -1) {
        score = Math.max(score, fuzzball.partial_ratio(restLC, c.characterLC) * 0.8);
      }

      // Alias check: an exact alias match is forced to 99 so it outranks
      // fuzzy coincidences but falls below a true exact name match (100).
      const aliases = CHARACTER_ALIASES[c.game as keyof typeof CHARACTER_ALIASES];
      if (aliases) {
        const charAliases = aliases[c.character as keyof typeof aliases] as string[] | undefined;
        if (charAliases) {
          for (const alias of charAliases) {
            const aliasScore = fuzzball.ratio(restLC, alias.toLowerCase());
            if (aliasScore === 100) {
              score = 99;
              break;
            }
            score = Math.max(score, aliasScore * 0.9);
          }
        }
      }

      if (score >= THRESHOLD || c.characterLC.startsWith(restLC)) {
        charSeen.add(key);
        results.push({
          kind: "character",
          game: c.game,
          characterName: c.character,
          href: `/framedata/${c.game}/${encodeURIComponent(c.character)}`,
          score,
        });
      }
    }

    // Step 4: character/move split
    // Try reading the first 1–3 words as a character name. If that scores
    // well and there are still words left over, treat the remainder as the
    // move query and restrict move results to that character.
    let characterFilter: string | null = null;
    let moveQuery = rest;

    const words = rest.split(/\s+/);
    let bestCharScore = 0;
    let bestCharName: string | null = null;
    let bestCharWordCount = 0;

    outer:
    for (let n = 1; n <= Math.min(3, words.length); n++) {
      const candidate = words.slice(0, n).join(" ").toLowerCase();

      for (const c of filtered.characters) {
        // Exact alias hit: treat as an unbeatable match
        // Check aliases FIRST, before fuzzy scoring, so "aki" > A.K.I.
        // always wins over fuzzball coincidences like "Akira".
        const aliases = CHARACTER_ALIASES[c.game as keyof typeof CHARACTER_ALIASES];
        if (aliases) {
          const charAliases = aliases[c.character as keyof typeof aliases] as string[] | undefined;
          if (charAliases) {
            for (const alias of charAliases) {
              if (candidate === alias.toLowerCase()) {
                bestCharScore = 100;
                bestCharName = c.character;
                bestCharWordCount = n;
                break outer; // bail out of both loops immediately
              }
            }
          }
        }

        let s = fuzzball.ratio(candidate, c.characterLC);
        if (c.characterLC.length > 2) {
          s = Math.max(s, fuzzball.partial_ratio(candidate, c.characterLC));
        }

        // Partial alias scoring (non-exact): still discounted
        if (aliases) {
          const charAliases = aliases[c.character as keyof typeof aliases] as string[] | undefined;
          if (charAliases) {
            for (const alias of charAliases) {
              s = Math.max(s, fuzzball.ratio(candidate, alias.toLowerCase()) * 0.9);
            }
          }
        }

        if (s > bestCharScore) {
          bestCharScore = s;
          bestCharName = c.character;
          bestCharWordCount = n;
        }
      }
    }

    // Only split if the character portion matches well AND there are move words left.
    if (bestCharScore >= 75 && bestCharWordCount < words.length) {
      characterFilter = resolveCharacterAlias(
        filtered.characters[0].game,
        bestCharName!
      );
      moveQuery = words.slice(bestCharWordCount).join(" ");
    }

    const moveQueryNorm = normalizeSearch(moveQuery);
    const moveSeen = new Set<string>();

    // Single-word queries use exact ratio; multi-word use partial_ratio
    // so "stand mk" can match "Stand Medium Kick" as a substring.
    const isSingleWord = !rest.includes(" ");

    // Step 4a: exact move match shortcut
    // If any move field normalises to exactly the query norm, skip fuzzy
    // scoring entirely and return those matches at score 100.
    const exactMoveMatches = filtered.moves.filter(
      (m) =>
        targetGames.has(m.game) &&
        (m.moveNameNorm === queryNorm ||
          m.cmnNameNorm === queryNorm ||
          m.numCmdNorm === queryNorm ||
          m.plnCmdNorm === queryNorm)
    );

    if (exactMoveMatches.length) {
      return exactMoveMatches.slice(0, MAX_RESULTS).map((m) => ({
        kind: "move" as const,
        game: m.game,
        characterName: m.character,
        characterState: m.state,
        moveName: m.moveName,
        cmnName: m.cmnName,
        href: `/movedetail/${m.game}/${encodeURIComponent(m.character)}/${m.state}/${encodeURIComponent(m.moveName)}`,
        plnCmd: m.plnCmd,
        numCmd: m.numCmd,
        score: 100,
      }));
    }

    // Step 4b: fuzzy move search
    for (const m of filtered.moves) {
      // Respect the character filter derived in Step 4.
      if (characterFilter && m.character !== characterFilter) continue;

      const key = `${m.game}/${m.character}/${m.state}/${m.moveName}`;
      if (moveSeen.has(key)) continue;

      const scoreFn = isSingleWord ? fuzzball.ratio : fuzzball.partial_ratio;

      // Score against all command/name variants and take the highest.
      let score = Math.max(
        scoreFn(moveQueryNorm, m.moveNameNorm),
        fuzzball.partial_ratio(moveQueryNorm, m.numCmdNorm),
        fuzzball.partial_ratio(moveQueryNorm, m.plnCmdNorm),
        m.cmnNameNorm ? scoreFn(moveQueryNorm, m.cmnNameNorm) : 0
      );

      // Boost exact phrase containment for move names
      if (m.moveNameNorm.includes(moveQueryNorm)) {
        score += 15;
      }

      // Penalize very short move hits when query is longer
      if (moveQueryNorm.length > 8 && m.moveNameNorm.length < 6) {
        score -= 10;
      }

      if (score >= THRESHOLD) {
        moveSeen.add(key);
        results.push({
          kind: "move",
          game: m.game,
          characterName: m.character,
          characterState: m.state,
          moveName: m.moveName,
          cmnName: m.cmnName,
          href: `/movedetail/${m.game}/${encodeURIComponent(m.character)}/${m.state}/${encodeURIComponent(m.moveName)}`,
          plnCmd: m.plnCmd,
          numCmd: m.numCmd,
          score,
        });
      }
    }

    // Step 5: sort
    // Rules (in priority order):
    //   a) Single-word query → characters always win (user is searching by name).
    //   b) Multi-word + no strong move match + no character filter → characters win.
    //   c) Multi-word + character filter detected → moves win (user typed "char move").
    //   d) Multi-word + strong move match → moves win.
    //   Within the winning kind, sort descending by score.
    //
    // "Strong move match" means at least one move's name/cmnName has a full
    // ratio ≥ 80 against the raw query — using full ratio (not partial) to
    // avoid substring false positives inflating the move tier.
    const hasDominantMoveMatch = results.some(
      (r) =>
        r.kind === "move" &&
        Math.max(
          fuzzball.ratio(restLC, r.moveName.toLowerCase()),
          r.cmnName ? fuzzball.ratio(restLC, r.cmnName.toLowerCase()) : 0
        ) >= 80
    );

    results.sort((a, b) => {
      if (isSingleWord) {
        // Rule (a)
        if (a.kind === "character" && b.kind !== "character") return -1;
        if (b.kind === "character" && a.kind !== "character") return 1;
      } else if (!hasDominantMoveMatch) {
        if (!characterFilter) {
          // Rule (b)
          if (a.kind === "character" && b.kind !== "character") return -1;
          if (b.kind === "character" && a.kind !== "character") return 1;
        } else {
          // Rule (c)
          if (a.kind === "move" && b.kind !== "move") return -1;
          if (b.kind === "move" && a.kind !== "move") return 1;
        }
      } else {
        // Rule (d)
        if (a.kind === "move" && b.kind !== "move") return -1;
        if (b.kind === "move" && a.kind !== "move") return 1;
      }
      return b.score - a.score;
    });

    // Step 6: deduplicate & cap
    // If moves already cover a character, drop the redundant character result
    // (avoids "Blanka" appearing twice when "SF6 Blanka fireball" returns
    // both a character card and move cards for Blanka).
    const charsCoveredByMoves = new Set(
      results
        .filter((r) => r.kind === "move")
        .map((r) => `${r.game}/${r.characterName}`)
    );

    return results
      .filter((r) => {
        if (r.kind === "character" && !isSingleWord) {
          const hasMoves = results.some((x) => x.kind === "move");
          if (hasMoves && charsCoveredByMoves.has(`${r.game}/${r.characterName}`)) {
            return false;
          }
        }
        return true;
      })
      .slice(0, MAX_RESULTS);
  }, [query, index]);

  const handleChange = useCallback((value: string) => setQuery(value), []);
  const clear = useCallback(() => setQuery(""), []);

  return { query, setQuery: handleChange, results, clear };
}